import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import TableComponent from '../../../components/Table/TableComponent';
import { downloadProjectReport } from '../../Services/ProjectReportService';
import { useGetProjectReport } from '../../../hooks/queries/reports/useGetProjectReport';
import { useReportDownloader } from '../../../hooks/useReportDownloader';
import { getProjectsByName } from '../../../services/ProjectService';
import { projectReportColumns as columns } from './ProjectReportColumns';
import './ProjectReportComponent.css';

const SORTABLE_FIELDS = new Set([
  'employeeName',
  'employeeCode',
  'projectName',
]);

const ProjectReportComponent = () => {
  const { download: downloadReport, isDownloading: downloading } =
    useReportDownloader(downloadProjectReport, 'project-report.xlsx');
  const [page, setPage] = useState(1);
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [employeeName, setEmployeeName] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [isManager, setIsManager] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectInput, setProjectInput] = useState('');
  const [projectLoading, setProjectLoading] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState({
    employeeName: '',
    employeeCode: '',
    isManager: false,
    projectIds: [],
  });

  const [sortBy, setSortBy] = useState('employeeName');
  const [sortDirection, setSortDirection] = useState('ASC');
  const [sortModel, setSortModel] = useState([
    { field: 'employeeName', sort: 'asc' },
  ]);

  const {
    rows,
    totalPage,
    isLoading: loader,
  } = useGetProjectReport({
    filters: appliedFilters,
    pageNumber: pageNo,
    pageSize,
    sortBy,
    sortDirection,
  });

  useEffect(() => {
    let active = true;
    const loadProjects = async () => {
      setProjectLoading(true);
      try {
        const response = await getProjectsByName(projectInput, 0, 20);
        if (!active) return;
        if (response?.isError === false) {
          setProjectOptions(response.data?.content || []);
        } else {
          setProjectOptions([]);
        }
      } catch (error) {
        if (active) setProjectOptions([]);
      } finally {
        if (active) setProjectLoading(false);
      }
    };

    const timer = setTimeout(loadProjects, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [projectInput]);

  const handleApplyFilters = () => {
    setPage(1);
    setPageNo(0);
    setAppliedFilters({
      employeeName: employeeName.replace(/\s+/g, ''),
      employeeCode: employeeCode.trim(),
      isManager,
      projectIds: selectedProjects.map((project) => project.id).filter(Boolean),
    });
  };

  const handleResetFilters = () => {
    setEmployeeName('');
    setEmployeeCode('');
    setIsManager(false);
    setSelectedProjects([]);
    setProjectInput('');
    setPage(1);
    setPageNo(0);
    setSortBy('employeeName');
    setSortDirection('ASC');
    setSortModel([{ field: 'employeeName', sort: 'asc' }]);
    setAppliedFilters({
      employeeName: '',
      employeeCode: '',
      isManager: false,
      projectIds: [],
    });
  };

  const pageChange = (event, value) => {
    setPage(value);
    setPageNo(value - 1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(1);
    setPageNo(0);
  };

  const handleSortModelChange = (model) => {
    const nextModel = Array.isArray(model) ? model : [];
    const activeSort = nextModel.find(
      (item) => item?.field && SORTABLE_FIELDS.has(item.field) && item.sort,
    );

    if (!activeSort) {
      setSortModel([{ field: 'employeeName', sort: 'asc' }]);
      setSortBy('employeeName');
      setSortDirection('ASC');
      setPage(1);
      setPageNo(0);
      return;
    }

    setSortModel([{ field: activeSort.field, sort: activeSort.sort }]);
    setSortBy(activeSort.field);
    setSortDirection(activeSort.sort === 'desc' ? 'DESC' : 'ASC');
    setPage(1);
    setPageNo(0);
  };

  const handleDownload = () =>
    downloadReport({
      ...appliedFilters,
      sortBy,
      sortDirection,
    });

  return (
    <Box className="project-report-page">
      <Box className="project-report-header">
        <Typography variant="h5" className="project-report-title">
          Project Report
        </Typography>
        <IconButton
          color="primary"
          onClick={handleDownload}
          disabled={downloading || loader}
          title="Download Excel"
        >
          {downloading ? (
            <CircularProgress size={22} />
          ) : (
            <SimCardDownloadIcon />
          )}
        </IconButton>
      </Box>

      <Grid container spacing={2} className="project-report-filters">
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Employee Name"
            value={employeeName}
            onChange={(e) => setEmployeeName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Employee Code"
            value={employeeCode}
            onChange={(e) => setEmployeeCode(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            multiple
            size="small"
            options={projectOptions}
            value={selectedProjects}
            loading={projectLoading}
            filterOptions={(options) => options}
            getOptionLabel={(option) => option?.projectName || ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onInputChange={(_, value) => setProjectInput(value)}
            onChange={(_, value) => setSelectedProjects(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Projects"
                placeholder="Search projects"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2} className="project-report-checkbox">
          <FormControlLabel
            control={
              <Checkbox
                checked={isManager}
                onChange={(e) => setIsManager(e.target.checked)}
              />
            }
            label="Is Manager"
          />
        </Grid>
        <Grid item xs={12} className="project-report-actions">
          <Button variant="contained" onClick={handleApplyFilters}>
            Apply
          </Button>
          <Button variant="outlined" onClick={handleResetFilters}>
            Reset
          </Button>
        </Grid>
      </Grid>

      <TableComponent
        loader={loader}
        TableRows={rows}
        TableColumns={columns}
        pageSize={pageSize}
        totalPage={totalPage}
        page={page}
        pageChange={pageChange}
        handlePageSizeChange={handlePageSizeChange}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        disableColumnFilter
        disableColumnMenu
      />
    </Box>
  );
};

export default ProjectReportComponent;
